import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@workos-inc/authkit-react";
import {
  Building2,
  Loader2,
  Lock,
  TriangleAlert,
  Unlink,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useIssueInvitePreview,
  useJoinIssueInvite,
} from "@/hooks/use-issue-invite";
import { ApiError } from "@/lib/api";

export const Route = createFileRoute("/i/$token")({
  component: IssueInvitePage,
});

/**
 * What an issue link opens. Public until the join: signed out, it names who
 * sent the link and the project, and nothing from the issue; signed in, it
 * joins and lands on the issue. A dead link says so before any sign-up. See
 * [[issue-invite-page.spec]].
 */
function IssueInvitePage() {
  const { token } = Route.useParams();
  const { isLoading, user } = useAuth();

  // Nothing is asked of the server until AuthKit knows who, if anyone, is
  // signed in: signed in joins, signed out only looks.
  if (isLoading) return <FullScreenSpinner />;
  return user ? <JoinLink token={token} /> : <SignedOutInvite token={token} />;
}

/** Why a join didn't land: a dead link, a removal, or anything else. */
type JoinFailure = "gone" | "removed" | "error";

function joinFailure(error: unknown): JoinFailure {
  if (error instanceof ApiError && error.status === 404) return "gone";
  if (
    error instanceof ApiError &&
    error.status === 403 &&
    (error.body as { code?: unknown } | null)?.code === "removed"
  ) {
    return "removed";
  }
  return "error";
}

function JoinLink({ token }: { token: string }) {
  const navigate = useNavigate();
  const { mutateAsync } = useJoinIssueInvite(token);
  // The outcome is kept here rather than read off the mutation: StrictMode's
  // rehearsal unmount detaches the hook from a mutation started in an effect,
  // so the hook's own state would never leave pending.
  const [failure, setFailure] = useState<JoinFailure | null>(null);

  const join = useCallback(async () => {
    setFailure(null);
    try {
      const { project_id, issue_id } = await mutateAsync();
      // Replace: Back from the issue skips the link.
      void navigate({
        to: "/projects/$projectId/issues/$issueId",
        params: { projectId: project_id, issueId: issue_id },
        replace: true,
      });
    } catch (error) {
      setFailure(joinFailure(error));
    }
  }, [mutateAsync, navigate]);

  // Once, however often this renders — StrictMode included, since the ref
  // outlives its double effect.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void join();
  }, [join]);

  if (failure === "gone") return <NoLongerWorks />;
  if (failure === "removed") return <Removed />;
  if (failure === "error") return <CouldNotOpen onRetry={() => void join()} />;
  return <FullScreenSpinner />;
}

function SignedOutInvite({ token }: { token: string }) {
  const preview = useIssueInvitePreview(token);
  const { signIn, signUp } = useAuth();
  const [leavingFor, setLeavingFor] = useState<"signup" | "login" | null>(null);

  if (preview.isPending || (preview.isError && preview.isFetching)) {
    return <FullScreenSpinner />;
  }
  if (preview.isError) {
    return preview.error instanceof ApiError && preview.error.status === 404 ? (
      <NoLongerWorks />
    ) : (
      <CouldNotOpen onRetry={() => void preview.refetch()} />
    );
  }

  const { inviter_name: inviter, project_name: projectName } = preview.data;
  // Back to this link once signed in, where the join then runs.
  const state = { returnTo: window.location.pathname };

  return (
    <PageFrame
      footer="Otus is where the project team tracks site issues. Post your photos here and they reach the team directly."
    >
      <span className="flex h-13 w-13 items-center justify-center rounded-full bg-secondary text-base font-semibold text-secondary-foreground">
        {inviter ? (
          initialsOf(inviter)
        ) : (
          <UserRound className="h-6 w-6" aria-hidden="true" />
        )}
      </span>
      <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.015em]">
        {inviter
          ? `${inviter} invited you to an issue`
          : "You've been invited to an issue"}
      </h1>
      <div className="flex flex-col gap-3">
        <FactRow icon={Building2}>
          <span className="block text-xs text-muted-foreground">Project</span>
          <span className="mt-px block text-sm font-medium">{projectName}</span>
        </FactRow>
        <FactRow icon={Lock}>
          <span className="text-sm leading-snug text-muted-foreground">
            Sign up or log in to see it. You only sign up once.
          </span>
        </FactRow>
      </div>
      <div className="mt-1 flex flex-col gap-2.5">
        <Button
          className="h-12 text-[15px]"
          disabled={leavingFor !== null}
          onClick={() => {
            setLeavingFor("signup");
            void signUp({ state });
          }}
        >
          {leavingFor === "signup" && <Loader2 className="animate-spin" />}
          Sign up
        </Button>
        <Button
          variant="outline"
          className="h-12 text-[15px]"
          disabled={leavingFor !== null}
          onClick={() => {
            setLeavingFor("login");
            void signIn({ state });
          }}
        >
          {leavingFor === "login" && <Loader2 className="animate-spin" />}
          Log in
        </Button>
      </div>
    </PageFrame>
  );
}

function NoLongerWorks() {
  return (
    <PageFrame footer="Already on this issue? You can still open it from your projects.">
      <IconBadge icon={Unlink} />
      <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.015em]">
        This link no longer works
      </h1>
      <p className="text-[15px] leading-normal text-muted-foreground">
        Issue links work for 7 days, and stop once the issue is closed.
      </p>
      <p className="text-[15px] leading-normal text-muted-foreground">
        Ask whoever sent it for a new invite.
      </p>
      <Button asChild variant="outline" className="mt-1.5 h-12 text-[15px]">
        <Link to="/">Open Otus</Link>
      </Button>
    </PageFrame>
  );
}

function Removed() {
  return (
    <PageFrame>
      <IconBadge icon={Lock} />
      <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.015em]">
        You no longer have access to this issue
      </h1>
      <p className="text-[15px] leading-normal text-muted-foreground">
        Someone on the project removed you from it.
      </p>
      <p className="text-[15px] leading-normal text-muted-foreground">
        If that&apos;s a mistake, ask them to invite you again.
      </p>
      <Button asChild variant="outline" className="mt-1.5 h-12 text-[15px]">
        <Link to="/">Go to my projects</Link>
      </Button>
    </PageFrame>
  );
}

/** No toaster on the public part of the app, so failures get a screen. */
function CouldNotOpen({ onRetry }: { onRetry: () => void }) {
  return (
    <PageFrame>
      <IconBadge icon={TriangleAlert} />
      <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.015em]">
        Couldn&apos;t open this link
      </h1>
      <p className="text-[15px] leading-normal text-muted-foreground">
        Check your connection and try again.
      </p>
      <Button className="mt-1.5 h-12 text-[15px]" onClick={onRetry}>
        Try again
      </Button>
    </PageFrame>
  );
}

/** The Otus wordmark, a card in the middle of the screen, and a line under it. */
function PageFrame({
  children,
  footer,
}: {
  children: ReactNode;
  footer?: string;
}) {
  return (
    <main
      className="flex min-h-dvh flex-col bg-background px-4"
      style={{
        paddingTop: "max(env(safe-area-inset-top, 0px), 1.25rem)",
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 1.75rem)",
      }}
    >
      <div className="mx-auto flex w-full max-w-[400px] flex-1 flex-col">
        {/* The wordmark is dark ink, so dark mode swaps in the light copy, as
            on the projects page. See [[theming]]. */}
        <div className="flex h-11 items-center">
          <img
            src="/otus-logo.svg"
            alt="OTUS"
            width={151}
            height={50}
            className="h-9 w-auto dark:hidden"
          />
          <img
            src="/otus-logo-dark.svg"
            alt="OTUS"
            width={151}
            height={50}
            className="hidden h-9 w-auto dark:block"
          />
        </div>
        <div className="flex flex-1 flex-col justify-center py-6">
          <section className="flex flex-col gap-4 rounded-2xl border border-border bg-card px-5 pt-6 pb-5">
            {children}
          </section>
        </div>
        {footer && (
          <p className="px-3 text-center text-[13px] leading-normal text-muted-foreground">
            {footer}
          </p>
        )}
      </div>
    </main>
  );
}

function IconBadge({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="flex h-13 w-13 items-center justify-center rounded-full bg-secondary text-secondary-foreground">
      <Icon className="h-6 w-6" aria-hidden="true" />
    </span>
  );
}

function FactRow({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
        <Icon className="h-[17px] w-[17px]" aria-hidden="true" />
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function FullScreenSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <Loader2
        className="h-7 w-7 animate-spin text-muted-foreground"
        aria-label="Loading"
      />
    </div>
  );
}

/** "Rajesh Kumar" → "RK"; one word gives one letter. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "";
  const last = words.length > 1 ? (words.at(-1)?.charAt(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}
